import { FC, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material'
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import {
  GRAY_COLORS_VARIANTS,
  NEGATIVE_COLORS_VARIANTS,
  PRIMARY_COLORS_VARIANTS,
  RED_COLORS_VARIANTS,
  TEXT_DARK_COLORS_VARIANTS,
} from '@/utils/constants/colors'
import { walletApi } from 'api/walletApi'
import { BottomSheet } from 'components/shared/bottom-drawer/BottomSheet'
import { BottomDrawerHeader } from 'components/shared/bottom-drawer/BottomDrawerHeader'
import { LoadingState } from 'types/prize-draw/prize-draw'
import { PRE_TOP_UP_TRANSACTION_ID_PARAM } from '@/utils/cashflowsReturn'
import { DarkInputField } from '../DarkInputField'
import { ComplianceMicroBar } from './ComplianceMicroBar'

const PRESET_AMOUNTS = [10, 20, 50, 100]

export interface TopUpBottomDrawerProps {
  open: boolean
  onClose: () => void

  returnUrlBase: string
  currentBalance?: number
  onBack?: () => void
  minimumAmount?: number
}

export const TopUpBottomDrawer: FC<TopUpBottomDrawerProps> = ({
  open,
  onClose,
  returnUrlBase,
  currentBalance,
  onBack,
  minimumAmount,
}) => {
  const [amount, setAmount] = useState<string>('')
  const [loadingState, setLoadingState] = useState<LoadingState>('idle')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [activeMethod, setActiveMethod] = useState<'card' | 'yaspa' | null>(null)

  const parsedAmount = parseFloat(amount)
  const isValidAmount = !Number.isNaN(parsedAmount) && parsedAmount >= 1

  const resetForm = () => {
    setAmount('')
    setLoadingState('idle')
    setErrorMessage('')
    setActiveMethod(null)
  }

  const handleClose = () => {
    if (loadingState === 'loading') return
    resetForm()
    onClose()
  }

  const handleBack = () => {
    if (loadingState === 'loading') return
    resetForm()
    onBack?.()
  }

  const handlePreset = (value: number) => {
    setAmount(String(value))
    setErrorMessage('')
  }

  const handleAmountChange = (value: string) => {
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value)
      setErrorMessage('')
    }
  }

  const handleProceed = async (method: 'card' | 'yaspa') => {
    if (!isValidAmount) return
    setActiveMethod(method)
    setLoadingState('loading')
    setErrorMessage('')
    try {
      const latestTransactions = await walletApi
        .getWalletTransactions({ limit: 1 })
        .catch(() => null)

      const returnUrl = new URL(returnUrlBase)
      if (latestTransactions) {
        const latestId = Math.max(0, ...latestTransactions.map((transaction) => transaction.id))
        returnUrl.searchParams.set(PRE_TOP_UP_TRANSACTION_ID_PARAM, String(latestId))
      }

      const redirectUrl =
        method === 'yaspa'
          ? (
              await walletApi.createYaspaDeposit(
                Math.round(parsedAmount * 100),
                returnUrl.toString(),
              )
            ).hosted_url
          : (await walletApi.createPaymentJob(parsedAmount, returnUrl.toString())).action_url

      sessionStorage.setItem('pendingDepositAmount', String(parsedAmount))
      window.location.href = redirectUrl
    } catch {
      setLoadingState('error')
      setErrorMessage('Could not start the payment. Please try again.')
    }
  }

  const afterTopUp =
    currentBalance !== undefined && isValidAmount ? currentBalance + parsedAmount : null

  return (
    <BottomSheet open={open} onClose={handleClose} zIndex={1400}>
      <BottomDrawerHeader>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          {onBack ? (
            <Button
              startIcon={<ChevronLeftOutlinedIcon />}
              onClick={handleBack}
              disabled={loadingState === 'loading'}
              size="small"
              sx={{
                bgcolor: 'transparent',
                color: TEXT_DARK_COLORS_VARIANTS[400],
                p: 0,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                minWidth: 0,
                '&:hover': { opacity: 0.7 },
              }}
            >
              Back
            </Button>
          ) : (
            <Box />
          )}
          <Button
            onClick={handleClose}
            disabled={loadingState === 'loading'}
            size="small"
            sx={{
              minWidth: 0,
              p: 0,
              color: TEXT_DARK_COLORS_VARIANTS[400],
              fontWeight: 700,
              fontSize: '1rem',
              boxShadow: 'none',
              '&:hover': { bgcolor: 'transparent', opacity: 0.7 },
            }}
          >
            ✕
          </Button>
        </Box>

        <Box display="flex" flexDirection="column" gap={0}>
          <Typography
            fontSize="0.625rem"
            fontWeight={300}
            color={TEXT_DARK_COLORS_VARIANTS[400]}
            textAlign="center"
          >
            Top Up Wallet
          </Typography>

          {currentBalance !== undefined && (
            <Typography
              fontSize="1.75rem"
              fontWeight={900}
              color={TEXT_DARK_COLORS_VARIANTS[500]}
              textAlign="center"
              mt={0.5}
            >
              Cash Balance: £{currentBalance.toFixed(2)}
            </Typography>
          )}
        </Box>
      </BottomDrawerHeader>

      <Box px={2.5} pt={2} pb={1.5} display="flex" flexDirection="column" gap={1.5}>
        <Box>
          <Typography
            fontSize="0.875rem"
            fontWeight={700}
            color={TEXT_DARK_COLORS_VARIANTS[400]}
            mb={1}
          >
            Amount to add
          </Typography>
          <DarkInputField
            fullWidth
            inputMode="decimal"
            value={amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            placeholder="0.00"
            disabled={loadingState === 'loading'}
            startAdornment={
              <InputAdornment position="start">
                <Typography sx={{ color: TEXT_DARK_COLORS_VARIANTS[280], fontWeight: 600 }}>
                  £
                </Typography>
              </InputAdornment>
            }
            sx={{
              '& input::placeholder': { color: GRAY_COLORS_VARIANTS[100], opacity: 1 },
            }}
          />
          {minimumAmount !== undefined && (
            <Typography fontSize="0.75rem" color={TEXT_DARK_COLORS_VARIANTS[350]} mt={0.75}>
              You need at least{' '}
              <Box component="span" fontWeight={700} color={TEXT_DARK_COLORS_VARIANTS[400]}>
                £{minimumAmount.toFixed(2)}
              </Box>{' '}
              to cover this purchase.
            </Typography>
          )}
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          {PRESET_AMOUNTS.map((preset) => (
            <Box
              key={preset}
              flex="1 1 auto"
              bgcolor={parsedAmount === preset ? PRIMARY_COLORS_VARIANTS[200] : 'white'}
              color={TEXT_DARK_COLORS_VARIANTS[400]}
              py={0.875}
              fontSize="0.875rem"
              fontWeight={700}
              borderRadius="999px"
              textAlign="center"
              border={`2px solid ${
                parsedAmount === preset ? PRIMARY_COLORS_VARIANTS[500] : GRAY_COLORS_VARIANTS[100]
              }`}
              onClick={() => handlePreset(preset)}
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: PRIMARY_COLORS_VARIANTS[500],
                  bgcolor: PRIMARY_COLORS_VARIANTS[200],
                },
              }}
            >
              £{preset}
            </Box>
          ))}
        </Box>

        {afterTopUp !== null && (
          <Box
            px={2}
            py={1.25}
            borderRadius="0.75rem"
            bgcolor={GRAY_COLORS_VARIANTS[50]}
            border={`1px solid ${GRAY_COLORS_VARIANTS[100]}`}
          >
            <Typography fontSize="0.8125rem" color={TEXT_DARK_COLORS_VARIANTS[350]}>
              After top up:{' '}
              <Box component="span" fontWeight={800} color={TEXT_DARK_COLORS_VARIANTS[500]}>
                £{afterTopUp.toFixed(2)}
              </Box>
            </Typography>
          </Box>
        )}

        {loadingState === 'error' && errorMessage && (
          <Box
            p={1.5}
            borderRadius="0.5rem"
            bgcolor={NEGATIVE_COLORS_VARIANTS[200]}
            border={`1px solid ${NEGATIVE_COLORS_VARIANTS[400]}`}
          >
            <Typography fontSize="0.8125rem" fontWeight={600} color={NEGATIVE_COLORS_VARIANTS[400]}>
              {errorMessage}
            </Typography>
          </Box>
        )}

        <Box
          p={1.5}
          borderRadius="0.5rem"
          bgcolor={GRAY_COLORS_VARIANTS[100]}
          display="flex"
          alignItems="flex-start"
          gap={1}
        >
          <OpenInNewIcon
            sx={{
              fontSize: '0.9rem',
              color: TEXT_DARK_COLORS_VARIANTS[280],
              mt: 0.25,
              flexShrink: 0,
            }}
          />
          <Typography fontSize="0.75rem" color={TEXT_DARK_COLORS_VARIANTS[280]} lineHeight="1.5">
            You will be securely redirected to the selected payment provider. You&apos;ll be brought
            back here once the payment is complete.
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleProceed('card')}
            disabled={!isValidAmount || loadingState === 'loading'}
            startIcon={
              loadingState === 'loading' && activeMethod === 'card' ? (
                <CircularProgress size={16} sx={{ color: TEXT_DARK_COLORS_VARIANTS[400] }} />
              ) : (
                <CreditCardOutlinedIcon sx={{ fontSize: '1.1rem !important' }} />
              )
            }
            sx={{
              py: 1.5,
              borderRadius: '0.75rem',
              fontWeight: 700,
              fontSize: '0.9375rem',
              textTransform: 'none',
              bgcolor: PRIMARY_COLORS_VARIANTS[500],
              color: TEXT_DARK_COLORS_VARIANTS[400],
              boxShadow: 'none',
              '&:hover': { bgcolor: PRIMARY_COLORS_VARIANTS[600], boxShadow: 'none' },
              '&:disabled': {
                bgcolor: GRAY_COLORS_VARIANTS[100],
                color: TEXT_DARK_COLORS_VARIANTS[350],
              },
            }}
          >
            {loadingState === 'loading' && activeMethod === 'card'
              ? 'Redirecting to payment…'
              : 'Deposit via Card'}
          </Button>
          <Typography
            fontSize="0.75rem"
            fontWeight={500}
            color={TEXT_DARK_COLORS_VARIANTS[280]}
            textAlign="center"
          >
            No credit cards · Apple Pay & Google Pay accepted
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleProceed('yaspa')}
            disabled={!isValidAmount || loadingState === 'loading'}
            startIcon={
              loadingState === 'loading' && activeMethod === 'yaspa' ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                <AccountBalanceOutlinedIcon sx={{ fontSize: '1.1rem !important' }} />
              )
            }
            sx={{
              py: 1.5,
              borderRadius: '0.75rem',
              fontWeight: 700,
              fontSize: '0.9375rem',
              textTransform: 'none',
              bgcolor: TEXT_DARK_COLORS_VARIANTS[400],
              color: 'white',
              boxShadow: 'none',
              '&:hover': { bgcolor: TEXT_DARK_COLORS_VARIANTS[500], boxShadow: 'none' },
              '&:disabled': {
                bgcolor: GRAY_COLORS_VARIANTS[100],
                color: TEXT_DARK_COLORS_VARIANTS[350],
              },
            }}
          >
            {loadingState === 'loading' && activeMethod === 'yaspa'
              ? 'Redirecting to your bank…'
              : 'Instant Bank Transfer'}
          </Button>
          <Typography
            fontSize="0.75rem"
            color={TEXT_DARK_COLORS_VARIANTS[280]}
            display="flex"
            alignItems="center"
            gap={0.5}
          >
            powered by
            <img
              src="/Yaspa-logo.png"
              alt="Yaspa"
              style={{ verticalAlign: 'middle', height: '14px', width: 'auto' }}
            />
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="text"
          onClick={onBack ? handleBack : handleClose}
          disabled={loadingState === 'loading'}
          sx={{
            fontWeight: 700,
            fontSize: '0.9375rem',
            textTransform: 'none',
            color: RED_COLORS_VARIANTS[500],
            '&:hover': { color: RED_COLORS_VARIANTS[600], bgcolor: 'transparent' },
          }}
        >
          {onBack ? 'Back' : 'Cancel'}
        </Button>

        <ComplianceMicroBar />
      </Box>
    </BottomSheet>
  )
}
